// Descontos.tsx
//
// Aba "Descontos", de "Descontos e Taxas". Cadastra a REGRA de desconto de cada
// turno de caixa: nome, tipo, forma + valor, vigência, teto por conta, a partir
// de quanto exige autorizador, e a classificação contábil (CMV ou despesa).
//
// Duas coisas desta tela existem para não repetir defeitos já conhecidos:
//
//  1. `forma` e `valor` são DOIS campos. Em produção hoje a mesma coluna guarda
//     "3" e "10%", e ninguém consegue somar, exibir nem auditar o que foi dado
//     de desconto sem adivinhar a unidade. Aqui a unidade é lida do select e
//     desenhada FORA do input — não dá para digitar "%" no valor.
//  2. Ela não promete cálculo. O aviso do topo diz, sem suavizar, que o valor
//     hoje é gravado como texto sem unidade e que acertar a conta de quem lê
//     esse texto é trabalho do OPS.
//
// Onde isso iria: acd."ActPeriodTypeInfo", periodTypeInfoType 1 ("discounts").
// A tabela e o tipo existem; a ROTA não. Por isso o Salvar monta o payload
// certo, manda para o shell e mostra a recusa nomeada que volta — nunca
// localStorage, nunca fechar fingindo que gravou.

import React, { useEffect, useMemo, useRef, useState } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/descontosSection";
import {
  AVISO_ARITMETICA,
  AUTORIZADOR_PADRAO_PERCENTUAL,
  FormDesconto,
  OPCOES_FORMA,
  OPCOES_TIPO_DESCONTO,
  PERIOD_TYPE_INFO_DESCONTOS,
  ROTA_QUE_FALTA,
  Tom,
  TurnoDeCaixa,
  aplicarRegra,
  contarLegadas,
  descontoVazio,
  lerTurnosDeCaixa,
  mostrarForma,
  mostrarReais,
  mostrarValor,
  payloadDeRegras,
  regraDoDesconto,
  rotuloDe,
  validarDesconto,
} from "./tipos";

type AvisoLocal = { tom: Tom; texto: string; itens?: string[] };

interface Props {
  decryptedInfoToken: any;
  handleSave: (objectType: string, payload?: any) => any;
}

const Descontos: React.FC<Props> = ({ decryptedInfoToken, handleSave }) => {
  const turnos: TurnoDeCaixa[] = useMemo(
    () => lerTurnosDeCaixa(decryptedInfoToken, PERIOD_TYPE_INFO_DESCONTOS),
    [decryptedInfoToken]
  );

  const [formularios, setFormularios] = useState<Record<string, FormDesconto>>({});
  const [regrasPorTurno, setRegrasPorTurno] = useState<Record<string, any[]>>({});
  const [avisos, setAvisos] = useState<Record<string, AvisoLocal>>({});
  const [salvando, setSalvando] = useState<Record<string, boolean>>({});

  // O salvamento é assíncrono e o usuário pode trocar de aba no meio dele.
  // Sem esta guarda, o setState de depois cai num componente desmontado.
  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  useEffect(() => {
    if (turnos.length === 0) return;
    const formulariosIniciais: Record<string, FormDesconto> = {};
    const regrasIniciais: Record<string, any[]> = {};
    const avisosIniciais: Record<string, AvisoLocal> = {};

    for (const turno of turnos) {
      formulariosIniciais[turno.uuid] = descontoVazio();
      regrasIniciais[turno.uuid] = turno.regras;
      if (turno.corrompida) {
        avisosIniciais[turno.uuid] = {
          tom: "atencao",
          texto:
            "O que está gravado neste turno não é um JSON válido, então as " +
            "regras não puderam ser lidas. Elas não aparecem na tabela abaixo — " +
            "e continuam no banco do jeito que estão.",
        };
      }
    }

    setFormularios(formulariosIniciais);
    setRegrasPorTurno(regrasIniciais);
    setAvisos(avisosIniciais);
  }, [turnos]);

  const mudar = (uuid: string, mudanca: Partial<FormDesconto>) => {
    setFormularios((atuais) => ({
      ...atuais,
      [uuid]: { ...(atuais[uuid] ?? descontoVazio()), ...mudanca },
    }));
  };

  /** Traz uma regra gravada de volta para o formulário, para corrigir/completar. */
  const editar = (uuid: string, regra: any) => {
    setFormularios((atuais) => ({
      ...atuais,
      [uuid]: {
        id: regra?.id ?? "",
        nome: regra?.nome ?? "",
        tipo: regra?.tipo ?? "livre",
        forma: regra?.forma === "reais" ? "reais" : "percentual",
        valor: regra?.valor != null ? String(regra.valor) : "",
        vigenciaInicio: regra?.vigenciaInicio ?? "",
        vigenciaFim: regra?.vigenciaFim ?? "",
        tetoPorConta: regra?.tetoPorConta != null ? String(regra.tetoPorConta) : "",
        exigeAutorizadorAcimaDe:
          regra?.exigeAutorizadorAcimaDe != null
            ? String(regra.exigeAutorizadorAcimaDe)
            : AUTORIZADOR_PADRAO_PERCENTUAL,
        cnpjConvenio: regra?.cnpjConvenio ?? "",
        // Regra antiga sem classificação não vira "despesa" de brinde: volta
        // vazia e o salvamento pede a escolha.
        entraNoCMV:
          regra?.entraNoCMV === true
            ? "cmv"
            : regra?.entraNoCMV === false
            ? "despesa"
            : "",
      },
    }));
  };

  const salvar = async (turno: TurnoDeCaixa) => {
    const uuid = turno.uuid;
    const form = formularios[uuid] ?? descontoVazio();

    const problemas = validarDesconto(form);
    if (problemas.length > 0) {
      setAvisos((atuais) => ({
        ...atuais,
        [uuid]: {
          tom: "erro",
          texto: "Corrija antes de salvar:",
          itens: problemas,
        },
      }));
      return;
    }

    // Sem o identificador do turno o payload sairia apontando para lugar
    // nenhum. Recusar por nome é melhor do que mandar `undefined` adiante.
    if (!uuid) {
      setAvisos((atuais) => ({
        ...atuais,
        [uuid]: {
          tom: "erro",
          texto:
            "Este turno de caixa veio do token sem identificador " +
            "(periodTypeUuid). Nada foi enviado.",
        },
      }));
      return;
    }

    const regras = aplicarRegra(regrasPorTurno[uuid] ?? [], regraDoDesconto(form));
    const payload = payloadDeRegras(turno, regras, PERIOD_TYPE_INFO_DESCONTOS);

    setSalvando((atuais) => ({ ...atuais, [uuid]: true }));
    try {
      const resposta = await handleSave("desconto", payload);
      if (!montado.current) return;

      // A recusa vem do shell, com o nome do que falta. Ela é a resposta REAL —
      // repetir aqui um "salvo com sucesso" por cima seria o modo de falha que
      // esta tela inteira existe para não repetir.
      if (resposta && resposta.ok === false) {
        setAvisos((atuais) => ({
          ...atuais,
          [uuid]: { tom: resposta.tom ?? "erro", texto: resposta.texto },
        }));
        return;
      }

      if (resposta && resposta.ok === true) {
        setAvisos((atuais) => ({
          ...atuais,
          [uuid]: { tom: "ok", texto: resposta.texto ?? "Regra gravada." },
        }));
        setRegrasPorTurno((atuais) => ({ ...atuais, [uuid]: regras }));
        setFormularios((atuais) => ({ ...atuais, [uuid]: descontoVazio() }));
        return;
      }

      // Nem ok nem recusa: o shell não disse o que aconteceu. Isso NÃO é
      // sucesso, e a tela não vai fingir que é.
      setAvisos((atuais) => ({
        ...atuais,
        [uuid]: {
          tom: "atencao",
          texto:
            "O salvamento não devolveu resposta, então não dá para afirmar que " +
            `algo foi gravado. Enquanto a rota ${ROTA_QUE_FALTA} não existir, ` +
            "trate a regra como não gravada.",
        },
      }));
    } catch (erro: any) {
      if (!montado.current) return;
      setAvisos((atuais) => ({
        ...atuais,
        [uuid]: {
          tom: "erro",
          texto:
            erro?.response?.data?.message ??
            erro?.message ??
            "Não foi possível salvar a regra de desconto.",
        },
      }));
    } finally {
      if (montado.current) {
        setSalvando((atuais) => ({ ...atuais, [uuid]: false }));
      }
    }
  };

  if (!decryptedInfoToken) {
    return (
      <AlfredoStyles.FieldsContainer>
        <AlfredoStyles.LoadingContainer>
          <AlfredoStyles.LoadingSpinner />
          <div>Carregando turnos de caixa…</div>
        </AlfredoStyles.LoadingContainer>
      </AlfredoStyles.FieldsContainer>
    );
  }

  return (
    <AlfredoStyles.FieldsContainer>
      <AlfredoStyles.Header>
        <AlfredoStyles.HeaderContent>
          <AlfredoStyles.HeaderTitle>
            <AlfredoStyles.MaterialIcons
              style={{ fontSize: "calc(var(--u) * 36)" }}
            >
              local_offer
            </AlfredoStyles.MaterialIcons>
            Descontos
          </AlfredoStyles.HeaderTitle>
          <AlfredoStyles.HeaderSubtitle>
            As regras de desconto de cada turno de caixa
          </AlfredoStyles.HeaderSubtitle>
        </AlfredoStyles.HeaderContent>
      </AlfredoStyles.Header>

      <AlfredoStyles.AvisoGrande>
        <AlfredoStyles.MaterialIcons
          style={{ fontSize: "calc(var(--u) * 28)" }}
        >
          warning_amber
        </AlfredoStyles.MaterialIcons>
        <div>
          <AlfredoStyles.AvisoGrandeTitulo>
            Isto cadastra a regra, não conserta a conta
          </AlfredoStyles.AvisoGrandeTitulo>
          {AVISO_ARITMETICA}
        </div>
      </AlfredoStyles.AvisoGrande>

      {/* Antes de qualquer digitação: onde este formulário iria parar, e por
          que ele ainda não vai. */}
      <AlfredoStyles.Aviso $tom="atencao">
        <strong>Nada nesta tela grava ainda.</strong> As regras iriam para{" "}
        <code>acd."ActPeriodTypeInfo"</code>, tipo {PERIOD_TYPE_INFO_DESCONTOS} (
        <code>discounts</code>) — a tabela e o tipo já existem, mas{" "}
        <strong>nenhum router do backend expõe {ROTA_QUE_FALTA}</strong>. Salvar
        monta o payload correto e recebe uma recusa nomeada: nada é enviado ao
        servidor e nada fica guardado no navegador. O que você digitar se perde
        ao sair da aba.
      </AlfredoStyles.Aviso>

      {turnos.length === 0 ? (
        <AlfredoStyles.EmptyState>
          <AlfredoStyles.EmptyStateIcon>🏷️</AlfredoStyles.EmptyStateIcon>
          <AlfredoStyles.EmptyStateTitle>
            Nenhum turno de caixa cadastrado
          </AlfredoStyles.EmptyStateTitle>
          <AlfredoStyles.EmptyStateDescription>
            Desconto pertence a um turno de caixa. Cadastre um em Lojas →
            Períodos para poder criar regras aqui.
          </AlfredoStyles.EmptyStateDescription>
        </AlfredoStyles.EmptyState>
      ) : (
        turnos.map((turno) => {
          const uuid = turno.uuid;
          const form = formularios[uuid] ?? descontoVazio();
          const regras = regrasPorTurno[uuid] ?? [];
          const aviso = avisos[uuid];
          const legadas = contarLegadas(regras);

          return (
            <AlfredoStyles.Card key={uuid || turno.nome}>
              <AlfredoStyles.CardTitulo>
                <AlfredoStyles.MaterialIcons>
                  schedule
                </AlfredoStyles.MaterialIcons>
                {turno.loja} — {turno.nome}
              </AlfredoStyles.CardTitulo>
              <AlfredoStyles.CardSubtitulo>
                {turno.existe
                  ? `${regras.length} regra(s) de desconto lida(s) do que está gravado`
                  : "Este turno ainda não tem nenhuma regra de desconto gravada"}
              </AlfredoStyles.CardSubtitulo>

              {/* O que está gravado hoje é um mapa `nome -> texto`, sem unidade
                  separada. A tela mostra o texto como está e não adivinha. */}
              {legadas > 0 && (
                <AlfredoStyles.Aviso $tom="atencao">
                  {legadas} regra(s) deste turno estão no formato antigo: o
                  banco guarda só o nome e um texto, sem dizer se é percentual
                  ou reais. Elas aparecem abaixo com o texto exato que está
                  gravado, e não dá para editá-las aqui — adivinhar a unidade
                  transformaria R$ 3,00 em 3% sem ninguém perceber. Recadastre a
                  regra pelo formulário, escolhendo a forma.
                </AlfredoStyles.Aviso>
              )}

              {/* ---------------- O QUE ESTÁ GRAVADO ---------------- */}
              <AlfredoStyles.Bloco>
                <AlfredoStyles.BlocoTitulo>
                  <AlfredoStyles.MaterialIcons
                    style={{ fontSize: "var(--fs-base)" }}
                  >
                    list_alt
                  </AlfredoStyles.MaterialIcons>
                  Regras gravadas (leitura)
                </AlfredoStyles.BlocoTitulo>

                {regras.length === 0 ? (
                  <AlfredoStyles.VazioNaTabela>
                    Nenhuma regra para mostrar.
                  </AlfredoStyles.VazioNaTabela>
                ) : (
                  <AlfredoStyles.Tabela>
                    <thead>
                      <tr>
                        <AlfredoStyles.TabelaCabecalho>
                          Nome
                        </AlfredoStyles.TabelaCabecalho>
                        <AlfredoStyles.TabelaCabecalho>
                          Tipo
                        </AlfredoStyles.TabelaCabecalho>
                        <AlfredoStyles.TabelaCabecalho>
                          Forma
                        </AlfredoStyles.TabelaCabecalho>
                        <AlfredoStyles.TabelaCabecalho>
                          Valor
                        </AlfredoStyles.TabelaCabecalho>
                        <AlfredoStyles.TabelaCabecalho>
                          Teto por conta
                        </AlfredoStyles.TabelaCabecalho>
                        <AlfredoStyles.TabelaCabecalho>
                          Autorizador acima de
                        </AlfredoStyles.TabelaCabecalho>
                        <AlfredoStyles.TabelaCabecalho>
                          Contábil
                        </AlfredoStyles.TabelaCabecalho>
                        <AlfredoStyles.TabelaCabecalho> </AlfredoStyles.TabelaCabecalho>
                      </tr>
                    </thead>
                    <tbody>
                      {regras.map((regra: any, indice: number) =>
                        regra?.legado === true ? (
                          // Regra do formato antigo. Só existe nome e um texto:
                          // as outras colunas ficam em "—" porque o dado não
                          // existe, e não porque está vazio.
                          <tr key={regra?.id ?? indice}>
                            <AlfredoStyles.TabelaCelula>
                              {regra.nome}
                            </AlfredoStyles.TabelaCelula>
                            <AlfredoStyles.TabelaCelula>—</AlfredoStyles.TabelaCelula>
                            <AlfredoStyles.TabelaCelula>—</AlfredoStyles.TabelaCelula>
                            <AlfredoStyles.TabelaCelula>
                              <code>{regra.valorBruto}</code>
                              <AlfredoStyles.Dica>
                                texto exato que está gravado
                              </AlfredoStyles.Dica>
                            </AlfredoStyles.TabelaCelula>
                            <AlfredoStyles.TabelaCelula>—</AlfredoStyles.TabelaCelula>
                            <AlfredoStyles.TabelaCelula>—</AlfredoStyles.TabelaCelula>
                            <AlfredoStyles.TabelaCelula>—</AlfredoStyles.TabelaCelula>
                            <AlfredoStyles.TabelaCelula>
                              <AlfredoStyles.Dica>
                                formato antigo
                              </AlfredoStyles.Dica>
                            </AlfredoStyles.TabelaCelula>
                          </tr>
                        ) : (
                          <tr key={regra?.id ?? indice}>
                            <AlfredoStyles.TabelaCelula>
                              {regra?.nome ?? "—"}
                            </AlfredoStyles.TabelaCelula>
                            <AlfredoStyles.TabelaCelula>
                              {rotuloDe(OPCOES_TIPO_DESCONTO, regra?.tipo)}
                            </AlfredoStyles.TabelaCelula>
                            {/* Forma e valor em colunas separadas: é o contrário
                                exato da coluna de produção que mistura "3" e "10%". */}
                            <AlfredoStyles.TabelaCelula>
                              {mostrarForma(regra?.forma)}
                            </AlfredoStyles.TabelaCelula>
                            <AlfredoStyles.TabelaCelula>
                              {mostrarValor(regra?.forma, regra?.valor)}
                            </AlfredoStyles.TabelaCelula>
                            <AlfredoStyles.TabelaCelula>
                              {mostrarReais(regra?.tetoPorConta)}
                            </AlfredoStyles.TabelaCelula>
                            <AlfredoStyles.TabelaCelula>
                              {mostrarValor(
                                "percentual",
                                regra?.exigeAutorizadorAcimaDe
                              )}
                            </AlfredoStyles.TabelaCelula>
                            <AlfredoStyles.TabelaCelula>
                              {regra?.entraNoCMV === true
                                ? "entra no CMV"
                                : regra?.entraNoCMV === false
                                ? "despesa"
                                : "—"}
                            </AlfredoStyles.TabelaCelula>
                            <AlfredoStyles.TabelaCelula>
                              <AlfredoStyles.TabelaAcao
                                type="button"
                                onClick={() => editar(uuid, regra)}
                              >
                                <AlfredoStyles.MaterialIcons
                                  style={{ fontSize: "var(--fs-sm)" }}
                                >
                                  edit
                                </AlfredoStyles.MaterialIcons>
                                Editar
                              </AlfredoStyles.TabelaAcao>
                            </AlfredoStyles.TabelaCelula>
                          </tr>
                        )
                      )}
                    </tbody>
                  </AlfredoStyles.Tabela>
                )}
              </AlfredoStyles.Bloco>

              {/* ---------------- FORMULÁRIO ---------------- */}
              <AlfredoStyles.Bloco>
                <AlfredoStyles.BlocoTitulo>
                  <AlfredoStyles.MaterialIcons
                    style={{ fontSize: "var(--fs-base)" }}
                  >
                    {form.id ? "edit" : "add_circle_outline"}
                  </AlfredoStyles.MaterialIcons>
                  {form.id ? "Editando uma regra" : "Nova regra de desconto"}
                </AlfredoStyles.BlocoTitulo>

                <AlfredoStyles.Grade>
                  <AlfredoStyles.Campo $largo>
                    <AlfredoStyles.Rotulo htmlFor={`nome-${uuid}`}>
                      Nome da regra
                    </AlfredoStyles.Rotulo>
                    <AlfredoStyles.Entrada
                      id={`nome-${uuid}`}
                      type="text"
                      value={form.nome}
                      placeholder="Ex.: Desconto de aniversário"
                      onChange={(e) => mudar(uuid, { nome: e.target.value })}
                    />
                  </AlfredoStyles.Campo>

                  <AlfredoStyles.Campo>
                    <AlfredoStyles.Rotulo htmlFor={`tipo-${uuid}`}>
                      Tipo
                    </AlfredoStyles.Rotulo>
                    <AlfredoStyles.Seletor
                      id={`tipo-${uuid}`}
                      value={form.tipo}
                      onChange={(e) =>
                        mudar(uuid, { tipo: e.target.value as any })
                      }
                    >
                      {OPCOES_TIPO_DESCONTO.map((opcao) => (
                        <option key={opcao.valor} value={opcao.valor}>
                          {opcao.rotulo}
                        </option>
                      ))}
                    </AlfredoStyles.Seletor>
                  </AlfredoStyles.Campo>

                  {/* FORMA e VALOR são dois campos, sempre. O "%" e o "R$" são
                      desenhados ao lado do input, nunca digitados dentro dele. */}
                  <AlfredoStyles.Campo>
                    <AlfredoStyles.Rotulo htmlFor={`forma-${uuid}`}>
                      Forma
                    </AlfredoStyles.Rotulo>
                    <AlfredoStyles.Seletor
                      id={`forma-${uuid}`}
                      value={form.forma}
                      onChange={(e) =>
                        mudar(uuid, { forma: e.target.value as any })
                      }
                    >
                      {OPCOES_FORMA.map((opcao) => (
                        <option key={opcao.valor} value={opcao.valor}>
                          {opcao.rotulo}
                        </option>
                      ))}
                    </AlfredoStyles.Seletor>
                  </AlfredoStyles.Campo>

                  <AlfredoStyles.Campo>
                    <AlfredoStyles.Rotulo htmlFor={`valor-${uuid}`}>
                      Valor
                    </AlfredoStyles.Rotulo>
                    <AlfredoStyles.CampoComUnidade>
                      <AlfredoStyles.Unidade>
                        {form.forma === "percentual" ? "%" : "R$"}
                      </AlfredoStyles.Unidade>
                      <AlfredoStyles.Entrada
                        id={`valor-${uuid}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.valor}
                        onChange={(e) => mudar(uuid, { valor: e.target.value })}
                      />
                    </AlfredoStyles.CampoComUnidade>
                    <AlfredoStyles.Dica>
                      Só o número. A unidade vem do campo "Forma".
                    </AlfredoStyles.Dica>
                  </AlfredoStyles.Campo>

                  <AlfredoStyles.Campo>
                    <AlfredoStyles.Rotulo htmlFor={`inicio-${uuid}`}>
                      Vigência — início
                    </AlfredoStyles.Rotulo>
                    <AlfredoStyles.Entrada
                      id={`inicio-${uuid}`}
                      type="date"
                      value={form.vigenciaInicio}
                      onChange={(e) =>
                        mudar(uuid, { vigenciaInicio: e.target.value })
                      }
                    />
                  </AlfredoStyles.Campo>

                  <AlfredoStyles.Campo>
                    <AlfredoStyles.Rotulo htmlFor={`fim-${uuid}`}>
                      Vigência — fim
                    </AlfredoStyles.Rotulo>
                    <AlfredoStyles.Entrada
                      id={`fim-${uuid}`}
                      type="date"
                      value={form.vigenciaFim}
                      onChange={(e) =>
                        mudar(uuid, { vigenciaFim: e.target.value })
                      }
                    />
                    <AlfredoStyles.Dica>
                      Sem data de fim, a regra não expira sozinha.
                    </AlfredoStyles.Dica>
                  </AlfredoStyles.Campo>

                  <AlfredoStyles.Campo>
                    <AlfredoStyles.Rotulo htmlFor={`teto-${uuid}`}>
                      Teto por conta
                    </AlfredoStyles.Rotulo>
                    <AlfredoStyles.CampoComUnidade>
                      <AlfredoStyles.Unidade>R$</AlfredoStyles.Unidade>
                      <AlfredoStyles.Entrada
                        id={`teto-${uuid}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.tetoPorConta}
                        onChange={(e) =>
                          mudar(uuid, { tetoPorConta: e.target.value })
                        }
                      />
                    </AlfredoStyles.CampoComUnidade>
                    <AlfredoStyles.Dica>
                      Em reais, sempre — mesmo quando o desconto é percentual.
                      Deixe vazio para não ter teto.
                    </AlfredoStyles.Dica>
                  </AlfredoStyles.Campo>

                  <AlfredoStyles.Campo>
                    <AlfredoStyles.Rotulo htmlFor={`autorizador-${uuid}`}>
                      Exige autorizador acima de
                    </AlfredoStyles.Rotulo>
                    <AlfredoStyles.CampoComUnidade>
                      <AlfredoStyles.Unidade>%</AlfredoStyles.Unidade>
                      <AlfredoStyles.Entrada
                        id={`autorizador-${uuid}`}
                        type="number"
                        min="0"
                        max="100"
                        step="0.5"
                        value={form.exigeAutorizadorAcimaDe}
                        onChange={(e) =>
                          mudar(uuid, {
                            exigeAutorizadorAcimaDe: e.target.value,
                          })
                        }
                      />
                    </AlfredoStyles.CampoComUnidade>
                    <AlfredoStyles.Dica>
                      <AlfredoStyles.Etiqueta>
                        padrão {AUTORIZADOR_PADRAO_PERCENTUAL}%
                      </AlfredoStyles.Etiqueta>{" "}
                      decisão do dono do produto, já tomada. Dá para mudar aqui.
                    </AlfredoStyles.Dica>
                  </AlfredoStyles.Campo>

                  {/* CNPJ só existe quando o desconto é de convênio: campo que
                      aparece sempre vira campo preenchido sem motivo. */}
                  {form.tipo === "convenio" && (
                    <AlfredoStyles.Campo>
                      <AlfredoStyles.Rotulo htmlFor={`cnpj-${uuid}`}>
                        CNPJ do convênio
                      </AlfredoStyles.Rotulo>
                      <AlfredoStyles.Entrada
                        id={`cnpj-${uuid}`}
                        type="text"
                        inputMode="numeric"
                        value={form.cnpjConvenio}
                        placeholder="00.000.000/0000-00"
                        onChange={(e) =>
                          mudar(uuid, { cnpjConvenio: e.target.value })
                        }
                      />
                      <AlfredoStyles.Dica>
                        14 dígitos. É a empresa parceira que responde pelo
                        desconto.
                      </AlfredoStyles.Dica>
                    </AlfredoStyles.Campo>
                  )}

                  <AlfredoStyles.Campo $largo>
                    <AlfredoStyles.Rotulo>
                      Classificação contábil
                    </AlfredoStyles.Rotulo>
                    <AlfredoStyles.GrupoRadio>
                      <AlfredoStyles.Linha>
                        <AlfredoStyles.Caixa
                          type="radio"
                          name={`cmv-${uuid}`}
                          checked={form.entraNoCMV === "cmv"}
                          onChange={() => mudar(uuid, { entraNoCMV: "cmv" })}
                        />
                        Entra no CMV
                      </AlfredoStyles.Linha>
                      <AlfredoStyles.Linha>
                        <AlfredoStyles.Caixa
                          type="radio"
                          name={`cmv-${uuid}`}
                          checked={form.entraNoCMV === "despesa"}
                          onChange={() => mudar(uuid, { entraNoCMV: "despesa" })}
                        />
                        Vira despesa
                      </AlfredoStyles.Linha>
                    </AlfredoStyles.GrupoRadio>
                    <AlfredoStyles.Dica>
                      Escolha obrigatória: o sistema não adivinha, e chutar aqui
                      erra o CMV do mês inteiro sem aparecer em lugar nenhum.
                    </AlfredoStyles.Dica>
                  </AlfredoStyles.Campo>
                </AlfredoStyles.Grade>

                <AlfredoStyles.AcaoRodape>
                  <AlfredoStyles.Button
                    type="button"
                    disabled={salvando[uuid]}
                    onClick={() => salvar(turno)}
                  >
                    <AlfredoStyles.MaterialIcons
                      style={{ fontSize: "var(--fs-base)" }}
                    >
                      save
                    </AlfredoStyles.MaterialIcons>
                    {salvando[uuid] ? "Salvando…" : "Salvar regra"}
                  </AlfredoStyles.Button>

                  {form.id && (
                    <AlfredoStyles.ButtonSecundario
                      type="button"
                      onClick={() =>
                        setFormularios((atuais) => ({
                          ...atuais,
                          [uuid]: descontoVazio(),
                        }))
                      }
                    >
                      Cancelar edição
                    </AlfredoStyles.ButtonSecundario>
                  )}
                </AlfredoStyles.AcaoRodape>

                {aviso && (
                  <AlfredoStyles.Aviso $tom={aviso.tom}>
                    {aviso.texto}
                    {aviso.itens && (
                      <AlfredoStyles.AvisoLista>
                        {aviso.itens.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </AlfredoStyles.AvisoLista>
                    )}
                  </AlfredoStyles.Aviso>
                )}
              </AlfredoStyles.Bloco>
            </AlfredoStyles.Card>
          );
        })
      )}
    </AlfredoStyles.FieldsContainer>
  );
};

export default Descontos;
